import React, { useState } from 'react'
import { Box } from '@mui/material'
import { EmtModal, EmtTypography } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { useEMTDispatch } from '@/store'
import { UserServiceHelper } from '@/modules/User'
import AnimalForm from './AnimalForm'
import { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'
import { buildAnimalRequestBody } from '@/helpers/animal/buildAnimalRequestBody'

interface AddAnimalModalProps {
  open: boolean
  close: () => void
  defaultImages?: boolean
  defaultDescription?: boolean
  onCreated?: (animalId: string) => void
  onSubmit?: (animal: AnimalData) => Promise<boolean | void>
  // When provided, the modal is in edit mode (form prefilled, "Modifier" title)
  initialValues?: AnimalData
}

const AddAnimalModal: React.FC<AddAnimalModalProps> = ({
  open,
  close,
  defaultImages,
  defaultDescription,
  onCreated,
  onSubmit,
  initialValues,
}) => {
  const { t } = useTranslation('connection')
  const dispatch = useEMTDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async (values: AnimalData) => {
    setIsLoading(true)

    try {
      if (onSubmit) {
        const shouldClose = await onSubmit(values)
        if (shouldClose !== false) close()
        return
      }

      const requestBody = buildAnimalRequestBody(values)

      if (initialValues?.uuid) {
        // @ts-expect-error
        await UserServiceHelper.dispatchUpdateAnimalAsync(dispatch)(initialValues.uuid, requestBody)
        close()
        return
      }

      // @ts-expect-error
      const created = await UserServiceHelper.dispatchCreateAnimalAsync(dispatch)(requestBody)

      if (created) {
        // Ajoute l'animal au store
        // setAnimals([...animals, created.id])

        // callback existant
        onCreated?.(created.id)
      }
      close()
    } catch (err) {
      console.error('Failed to add animal:', err)
      // TODO: Add error handling
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    close()
  }

  return (
    <EmtModal
      open={open}
      onClose={handleCancel}
      disableScrollLock
      sx={{
        '& .inner-box': { width: '600px', maxWidth: '90vw', backgroundColor: '#F9F9F9' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <EmtTypography variant="h4" fontWeight={800}>
          {initialValues ? t('Modifier un animal') : t('Ajouter un animal')}
        </EmtTypography>
        <AnimalForm
          key={initialValues?.uuid || 'new'}
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          color="primary"
          isLoading={isLoading}
          defaultImages={defaultImages}
          defaultDescription={
            defaultDescription
              ? "La description de cet animal n'a pas été donnée par son propriétaire. Si vous en avez besoin, vous pouvez l'encourager à vous donner une description via la messagerie."
              : undefined
          }
        />
      </Box>
    </EmtModal>
  )
}

export default AddAnimalModal
